// Dedicated service pages — each targets a specific care type for local SEO.
export const SERVICES = [
  {
    slug: "skilled-nursing",
    name: "Skilled Nursing Care",
    shortName: "Skilled Nursing",
    icon: "Stethoscope",
    image: "/img/care-4.png",
    metaTitle: "Skilled Nursing Care at Home in Northern Virginia | Esprit Home Healthcare",
    metaDescription: "Licensed RNs provide skilled nursing at home in Northern Virginia — wound care, medication management, IV therapy, and post-hospital recovery. Call (703) 998-7400.",
    tagline: "Rehab-quality clinical care, delivered in the comfort of home.",
    intro: "Esprit Skilled Home Care is appropriate whenever a person has great difficulty leaving home due to impaired mobility, decreased endurance, or profound weakness and has medical conditions requiring professional nursing at home.",
    overview: [
      "Our Registered Nurses bring hospital-grade clinical expertise directly to your living room. From the moment you're discharged until you're back on your feet, an Esprit nurse coordinates every detail of your recovery.",
      "We work closely with your physician and our interdisciplinary team to design a personalized care plan, then deliver it with compassion and precision so you can heal safely at home.",
    ],
    benefits: [
      "Comprehensive nursing assessments",
      "Wound care & dressing changes",
      "Medication setup & management",
      "IV therapy & injections",
      "Disease & post-surgical management",
      "Patient & family education",
    ],
    conditions: [
      "Recovering after surgery or hospitalization",
      "Managing chronic conditions (diabetes, CHF, COPD)",
      "Complex wound or ostomy care",
      "New medication regimens",
    ],
    faqs: [
      { q: "Does insurance cover skilled nursing at home?", a: "Esprit is a preferred provider for most major insurance plans, and skilled nursing is often covered when ordered by your physician. Our team will verify your benefits at no cost before care begins." },
      { q: "How quickly can skilled nursing start?", a: "In most cases we can begin care within 24–48 hours. One call to (703) 998-7400 starts the process, often before you even leave the hospital." },
      { q: "Will I have the same nurse each visit?", a: "We prioritize continuity of care and match you with a consistent clinical team whenever possible." },
    ],
    related: ["physical-therapy", "medical-social-worker", "personal-care"],
  },
  {
    slug: "physical-therapy",
    name: "Physical Therapy at Home",
    shortName: "Physical Therapy",
    icon: "Activity",
    image: "/img/care-2.png",
    metaTitle: "In-Home Physical Therapy in Northern Virginia | Esprit Home Healthcare",
    metaDescription: "Restore strength, balance, and mobility with in-home physical therapy across Northern Virginia. Fall prevention & post-surgical rehab. Call (703) 998-7400.",
    tagline: "Regain strength, balance, and independence — without leaving home.",
    intro: "Our licensed physical therapists deliver rehab-quality treatment in your home, designing individualized programs that rebuild strength, improve mobility, and reduce the risk of falls.",
    overview: [
      "Whether you're recovering from joint replacement, a stroke, or simply experiencing decreased endurance, in-home physical therapy lets you progress at your own pace in a familiar, comfortable setting.",
      "Therapy at home also means no transportation hassles — and care that's tailored to the exact environment where you live and move every day.",
    ],
    benefits: [
      "Post-surgical & orthopedic rehab",
      "Fall prevention & balance training",
      "Strength & endurance building",
      "Gait & mobility training",
      "Pain management techniques",
      "Home safety assessments",
    ],
    conditions: [
      "After hip or knee replacement",
      "Stroke or neurological recovery",
      "Frequent falls or unsteady balance",
      "General weakness or deconditioning",
    ],
    faqs: [
      { q: "Do I need a doctor's referral for home physical therapy?", a: "A physician order is typically required for insurance-covered therapy. Our team helps coordinate this with your doctor." },
      { q: "What equipment is needed?", a: "Our therapists bring the tools they need and use items already in your home, keeping therapy practical and realistic." },
    ],
    related: ["occupational-therapy", "skilled-nursing", "speech-therapy"],
  },
  {
    slug: "occupational-therapy",
    name: "Occupational Therapy at Home",
    shortName: "Occupational Therapy",
    icon: "HandHeart",
    image: "/img/care-3.png",
    metaTitle: "In-Home Occupational Therapy in Northern Virginia | Esprit Home Healthcare",
    metaDescription: "Occupational therapy at home in Northern Virginia helps you regain independence in daily activities. Call (703) 998-7400 to learn more.",
    tagline: "Reclaim the daily activities that matter most to you.",
    intro: "Occupational therapy helps you relearn and adapt the everyday tasks — dressing, bathing, cooking, and more — that keep you living independently and confidently at home.",
    overview: [
      "Our occupational therapists assess how you perform daily activities and create practical strategies and adaptations so you can do them safely and with greater ease.",
      "We focus on what you want to be able to do — and build a plan to get you there.",
    ],
    benefits: [
      "Activities of daily living (ADL) training",
      "Fine motor & coordination therapy",
      "Adaptive equipment recommendations",
      "Energy conservation techniques",
      "Cognitive & memory strategies",
      "Home modification guidance",
    ],
    conditions: [
      "Difficulty with dressing, bathing, or grooming",
      "Recovery from stroke or injury",
      "Arthritis or limited hand function",
      "Cognitive changes affecting daily tasks",
    ],
    faqs: [
      { q: "How is occupational therapy different from physical therapy?", a: "Physical therapy focuses on movement, strength, and mobility; occupational therapy focuses on the practical skills needed for daily living. Many patients benefit from both." },
    ],
    related: ["physical-therapy", "speech-therapy", "personal-care"],
  },
  {
    slug: "speech-therapy",
    name: "Speech Therapy at Home",
    shortName: "Speech Therapy",
    icon: "MessageCircle",
    image: "/img/care-5.png",
    metaTitle: "In-Home Speech Therapy in Northern Virginia | Esprit Home Healthcare",
    metaDescription: "Speech-language pathology at home in Northern Virginia for communication and swallowing disorders. Call (703) 998-7400.",
    tagline: "Restore communication and swallowing safely at home.",
    intro: "Our speech-language pathologists evaluate and treat communication and swallowing disorders, helping patients regain confidence and safety in everyday interactions.",
    overview: [
      "Following a stroke, neurological event, or illness, speech therapy can be life-changing — improving the ability to speak, understand, and swallow safely.",
      "We deliver these specialized services in the comfort of your home as part of a coordinated care plan.",
    ],
    benefits: [
      "Speech & language rehabilitation",
      "Swallowing (dysphagia) therapy",
      "Cognitive-communication therapy",
      "Voice therapy",
      "Caregiver coaching",
      "Safe-eating strategies",
    ],
    conditions: [
      "Speech changes after a stroke",
      "Difficulty swallowing safely",
      "Memory & communication challenges",
      "Voice or language disorders",
    ],
    faqs: [
      { q: "Can speech therapy help with swallowing problems?", a: "Yes. Our speech-language pathologists are trained to evaluate and treat dysphagia (swallowing difficulty), which is critical for safe nutrition and preventing complications." },
    ],
    related: ["skilled-nursing", "occupational-therapy", "medical-social-worker"],
  },
  {
    slug: "medical-social-worker",
    name: "Medical Social Worker Services",
    shortName: "Medical Social Work",
    icon: "ShieldCheck",
    image: "/img/care-6.png",
    metaTitle: "Medical Social Worker Services at Home | Northern Virginia | Esprit",
    metaDescription: "Esprit medical social workers help Northern Virginia families navigate resources, insurance, and care planning. Call (703) 998-7400.",
    tagline: "Guidance and support for the whole family.",
    intro: "Our Medical Social Workers help you and your family navigate the emotional, financial, and logistical challenges that come with managing care at home.",
    overview: [
      "From connecting you with community resources to coordinating long-term care planning and insurance questions, our social workers make a complex journey feel manageable.",
      "You focus on your loved one — we'll help with everything else.",
    ],
    benefits: [
      "Care planning & coordination",
      "Community resource navigation",
      "Insurance & benefits guidance",
      "Emotional & family support",
      "Long-term care planning",
      "Caregiver support resources",
    ],
    conditions: [
      "Families overwhelmed by care decisions",
      "Navigating insurance or benefits",
      "Planning for long-term care",
      "Coordinating multiple providers",
    ],
    faqs: [
      { q: "Is there a cost for medical social work services?", a: "Medical social work is frequently included as part of a covered home health plan of care. We'll confirm your coverage up front." },
    ],
    related: ["skilled-nursing", "personal-care", "private-duty-care"],
  },
  {
    slug: "personal-care",
    name: "Personal Care Services",
    shortName: "Personal Care",
    icon: "Heart",
    image: "/img/care-1.png",
    metaTitle: "Personal Care Aide Services at Home | Northern Virginia | Esprit",
    metaDescription: "Compassionate personal care aides assist with daily living across Northern Virginia — bathing, grooming, meals, and companionship. Call (703) 998-7400.",
    tagline: "Every person is a unique individual.",
    intro: "We deliver personal care services tailored to meet the needs of you or your loved ones, assigning a licensed aide who is the 'right fit' to assist with everyday tasks related to health and wellness.",
    overview: [
      "Our aides take the time to truly get to know you — your routines, preferences, and personality — so the care you receive feels personal, respectful, and dignified.",
      "Families enjoy the peace of mind of seeing their loved one's quality of life improve, right at home.",
    ],
    benefits: [
      "Bathing, grooming & dressing",
      "Meal preparation & feeding support",
      "Light housekeeping & laundry",
      "Mobility & transfer assistance",
      "Medication reminders",
      "Companionship & engagement",
    ],
    conditions: [
      "Difficulty with daily self-care",
      "Need for companionship & supervision",
      "Recovering at home and needing extra help",
      "Family caregivers needing respite",
    ],
    faqs: [
      { q: "Are your personal care aides licensed and screened?", a: "Yes. Every aide is trained, licensed where required, background-checked, and carefully matched to your needs." },
      { q: "Can I choose how many hours of care I receive?", a: "Absolutely. We offer flexible scheduling from a few hours a week up to around-the-clock private duty care." },
    ],
    related: ["private-duty-care", "skilled-nursing", "occupational-therapy"],
  },
  {
    slug: "private-duty-care",
    name: "Private Duty Care",
    shortName: "Private Duty Care",
    icon: "Users",
    image: "/img/care-6.png",
    metaTitle: "Private Duty Home Care in Northern Virginia | Esprit Home Healthcare",
    metaDescription: "Reliable private duty caregivers provide one-on-one support and companionship across Northern Virginia. Hourly to 24/7 care. Call (703) 998-7400.",
    tagline: "A reliable, compassionate companion you can trust.",
    intro: "Families who choose private duty care enjoy the benefit of seeing their loved one's quality of life improve at home, with the security of knowing they're kept safe by a competent, compassionate companion.",
    overview: [
      "Private duty care offers dedicated one-on-one support — from a few hours a day to 24/7 live-in care — tailored entirely around your family's schedule and needs.",
      "Being assured that your loved one has a reliable, highly trained companion brings genuine peace of mind.",
    ],
    benefits: [
      "Hourly to 24/7 / live-in care",
      "One-on-one dedicated attention",
      "Companionship & supervision",
      "Errands & transportation support",
      "Personal care assistance",
      "Flexible, family-centered scheduling",
    ],
    conditions: [
      "Need for continuous supervision",
      "Dementia or Alzheimer's support",
      "Living alone and needing companionship",
      "Family caregivers needing relief",
    ],
    faqs: [
      { q: "What's the difference between personal care and private duty?", a: "Personal care typically covers scheduled assistance with daily tasks; private duty offers more extensive, dedicated one-on-one support, up to live-in or 24/7 care." },
    ],
    related: ["personal-care", "medical-social-worker", "skilled-nursing"],
  },
];

export const getService = (slug) => SERVICES.find((s) => s.slug === slug);
